import './dashboard.css';
import './auth.css';
import './owner/owner.css';
import './chrome.css';
import { initMonitoring, reportError } from './monitoring.js';
import { supabase } from './supabaseClient.js';
import { createAuthScreen } from './auth/authScreen.js';
import { createStorageAdapter } from './storage/storageAdapter.js';
import { createOwnerDashboard } from './owner/ownerDashboard.js';
import { APP_NAME, APP_MARK, APP_TAGLINE } from './brand.js';

initMonitoring();
// vendor-app-shell.js is a classic (non-module) script, so it reads the brand off window rather
// than importing brand.js directly.
window.__APPS2027_BRAND__ = { name: APP_NAME, mark: APP_MARK, tagline: APP_TAGLINE };
document.title = APP_NAME;

const authRoot = document.getElementById('auth-root');
const dashboardRoot = document.getElementById('apps2027-root');
const ownerRoot = document.getElementById('owner-root');
const utilityBar = document.getElementById('utility-bar');
const signOutButton = document.getElementById('sign-out-button');
const ownerNavButton = document.getElementById('owner-nav-button');

let dashboardBooted = false;
let ownerDashboard = null;
let isSigningOut = false;

function showOnly(section) {
  authRoot.style.display = section === 'auth' ? '' : 'none';
  dashboardRoot.style.display = section === 'dashboard' ? '' : 'none';
  ownerRoot.style.display = section === 'owner' ? '' : 'none';
  utilityBar.style.display = section === 'auth' ? 'none' : '';
  document.body.classList.toggle('auth-mode', section === 'auth');
}

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

async function bootDashboard(session) {
  if (dashboardBooted) {
    showOnly('dashboard');
    return;
  }
  dashboardBooted = true;

  const userId = session.user.id;
  const storageAdapter = createStorageAdapter({ supabase, userId });
  window.__APPS2027_USER_ID__ = userId;
  window.__APPS2027_STORAGE_ADAPTER__ = storageAdapter;
  window.__APPS2027_USER_EMAIL__ = session.user.email;
  window.__APPS2027_DISPLAY_NAME__ = session.user.user_metadata?.display_name || session.user.email;

  try {
    window.__APPS2027_CLOUD_HISTORY__ = await storageAdapter.fetchHistory();
  } catch {
    // Leave undefined — the dashboard falls back to this user's namespaced local cache.
  }

  storageAdapter.recordDailyActivity().catch(() => {});
  storageAdapter.flushPending().catch(() => {});

  showOnly('dashboard');

  await loadScriptOnce('/vendor-engine-quant.js');
  await loadScriptOnce('/vendor-engine-finance.js');
  await loadScriptOnce('/vendor-app-shell.js');

  const ownerEmailHint = import.meta.env.VITE_OWNER_EMAIL;
  if (ownerEmailHint && session.user.email === ownerEmailHint) {
    ownerNavButton.style.display = 'inline-flex';
  }
}

function showAuthScreen() {
  showOnly('auth');
  authScreenApi.render();
}

async function handleAuthenticated(session) {
  sessionStorage.removeItem('apps2027.sessionExpired');
  await bootDashboard(session);
}

const authScreenApi = createAuthScreen({
  container: authRoot,
  supabase,
  onAuthenticated: handleAuthenticated,
});

signOutButton.addEventListener('click', async () => {
  isSigningOut = true;
  await supabase.auth.signOut();
  // Full reload wipes all in-memory dashboard state (the vendor script keeps its data in a
  // module-scoped closure), so no trace of this user's session survives for the next sign-in
  // on this browser/tab.
  window.location.reload();
});

ownerNavButton.addEventListener('click', () => {
  if (!ownerDashboard) {
    ownerDashboard = createOwnerDashboard({
      container: ownerRoot,
      supabase,
      onExit: () => showOnly('dashboard'),
    });
  }
  showOnly('owner');
  ownerDashboard.load();
});

supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'PASSWORD_RECOVERY') {
    showOnly('auth');
    authScreenApi.showPasswordRecovery();
    return;
  }
  if (event === 'SIGNED_OUT' && dashboardBooted && !isSigningOut) {
    sessionStorage.setItem('apps2027.sessionExpired', '1');
    window.location.reload();
  }
});

(async function init() {
  const { data } = await supabase.auth.getSession();
  if (data?.session) {
    // getSession() only checks the local token's expiry claim — it can't tell if the
    // session was revoked server-side (e.g. the account no longer exists, or was signed
    // out elsewhere). getUser() round-trips to Supabase and actually validates it.
    const { data: userCheck, error: userCheckError } = await supabase.auth.getUser();
    if (userCheck?.user && !userCheckError) {
      await bootDashboard(data.session);
      return;
    }
    // Could be a genuinely revoked session (expected) or a backend/network failure
    // masquerading as one (not expected) — report it so the two are distinguishable.
    if (userCheckError) reportError(userCheckError, { op: 'init:getUser' });
    await supabase.auth.signOut();
    sessionStorage.setItem('apps2027.sessionExpired', '1');
  }
  if (sessionStorage.getItem('apps2027.sessionExpired')) {
    sessionStorage.removeItem('apps2027.sessionExpired');
    showOnly('auth');
    authScreenApi.showExpiredSessionNotice();
    return;
  }
  showAuthScreen();
})();
